import React, { useMemo } from "react";
import { Table, Badge, Button, Spinner, Row, Col } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { saveFinalDecision as onSaveFinalDecision } from "store/actions";
import { toast } from "react-toastify";

// Utilitaires PDF (à importer selon tes fichiers)
// import { generateExclusionPDF } from "./reports/ExclusionListPDF";

const FinalDeliberationManager = ({ sessionResults, sessionInfo, filters }) => {
    const dispatch = useDispatch();

    // Récupération de l'état de chargement depuis Redux
    const { loadingFinalDecision } = useSelector(state => ({
        loadingFinalDecision: state.sessionReducer.loadingFinalDecision,
    }));

    /**
     * LOGIQUE MÉTIER : Application des Articles 11 & 12
     * Calcul du verdict final basé sur la moyenne, les crédits et l'historique
     */
    const processedData = useMemo(() => {
        return (sessionResults || []).map(res => {
            const avg = parseFloat(res.moyenne) || 0;
            const credits = parseInt(res.total_credits_validated || res.total_credits || 0);
            
            // Nombre d'inscriptions (doit être fourni par le backend via l'objet student)
            const nbInscriptions = res.student?.nb_inscriptions || 1; 

            let verdict = "REDOUBLANT";
            let color = "warning";

            // 1. ADMIS : Moyenne >= 10 et 60 crédits
            if (avg >= 10 && credits === 60) {
                verdict = "ADMIS";
                color = "success";
            } 
            // 2. ADMIS SOUS CRÉDIT : Moyenne >= 10 et crédits entre 48 et 59
            else if (avg >= 10 && credits >= 48) {
                verdict = "ADMIS SOUS CRÉDIT";
                color = "info";
            } 
            // 3. EXCLU : Si échec ET a déjà épuisé ses chances (Art 11 & 12)
            // Exemple : Maximum 2 inscriptions en L1
            else if (nbInscriptions >= 2) { 
                verdict = "EXCLU";
                color = "danger";
            } 
            // 4. REDOUBLANT : Par défaut (si échec mais < 2 inscriptions)
            else {
                verdict = "REDOUBLANT";
                color = "warning";
            }

            return {
                ...res,
                final_status: verdict,
                status_color: color,
                nb_inscriptions: nbInscriptions,
                student_full_name: `${res.student?.last_name || ""} ${res.student?.first_name || ""}`
            };
        });
    }, [sessionResults]);

    /**
     * ACTION : Enregistrer via Redux Saga
     */
    const handleSaveDecisions = () => {
        if (!filters?.study_level_id || !sessionInfo?.academic_year_id) {
            toast.error("Données de session ou de niveau manquantes.");
            return;
        }

        const payload = {
            academic_year_id: sessionInfo.academic_year_id,
            study_level_id: filters.study_level_id,
            decisions: processedData.map(d => ({
                student_id: d.student_id,
                status: d.final_status, // Sera mappé vers final_status dans le controller
                average: d.moyenne,
                credits: d.total_credits_validated || d.total_credits,
                nb_inscriptions: d.nb_inscriptions
            }))
        };

        if (window.confirm("Voulez-vous confirmer et enregistrer ces verdicts ? Cette action clôture l'année.")) {
            dispatch(onSaveFinalDecision(payload));
        }
    };

    // Statistiques rapides
    const stats = {
        exclus: processedData.filter(d => d.final_status === "EXCLU").length,
        admis: processedData.filter(d => d.final_status.includes("ADMIS")).length,
        redoublants: processedData.filter(d => d.final_status === "REDOUBLANT").length
    };

    return (
        <React.Fragment>
            <div className="mb-4">
                <Row className="align-items-center bg-light p-3 rounded border">
                    <Col md={6}>
                        <h5 className="text-primary mb-1">
                            <i className="bx bx-gavel me-2"></i>Délibération Annuelle
                        </h5>
                        <p className="text-muted small mb-0">
                            Analyse des résultats définitifs selon le régime LMD.
                        </p>
                    </Col>
                    <Col md={6} className="text-end">
                        <div className="d-flex gap-2 justify-content-end">
                            <Button 
                                color="danger" 
                                outline 
                                size="sm"
                                disabled={stats.exclus === 0}
                                // onClick={() => generateExclusionPDF(processedData.filter(d => d.final_status === "EXCLU"))}
                            >
                                <i className="bx bx-user-x me-1"></i> Liste des Exclus ({stats.exclus})
                            </Button>
                            <Button 
                                color="success" 
                                size="sm"
                                onClick={handleSaveDecisions}
                                disabled={loadingFinalDecision || processedData.length === 0}
                            >
                                {loadingFinalDecision ? (
                                    <Spinner size="sm" />
                                ) : (
                                    <><i className="bx bx-save me-1"></i> Enregistrer les Verdicts</>
                                )}
                            </Button>
                        </div>
                    </Col>
                </Row>

                <div className="d-flex gap-3 mt-2 small fw-medium">
                    <span className="text-success"><i className="bx bxs-circle me-1"></i>Admis: {stats.admis}</span>
                    <span className="text-warning"><i className="bx bxs-circle me-1"></i>Redoublants: {stats.redoublants}</span>
                    <span className="text-danger"><i className="bx bxs-circle me-1"></i>Exclus: {stats.exclus}</span>
                </div>
            </div>

            <div className="table-responsive">
                <Table className="table table-bordered table-hover align-middle">
                    <thead className="table-light text-center">
                        <tr>
                            <th style={{ width: "30%" }}>Étudiant</th>
                            <th>Moyenne Annuelle</th>
                            <th>Crédits Validés</th>
                            <th>Inscription</th>
                            <th>Verdict Final</th>
                            <th>Relevé</th>
                        </tr>
                    </thead>
                    <tbody>
                        {processedData.length > 0 ? (
                            processedData.map((res, idx) => (
                                <tr key={idx} className="text-center">
                                    <td className="text-start">
                                        <div className="fw-bold">{res.student_full_name}</div>
                                        <small className="text-muted">{res.student?.student_number}</small>
                                    </td>
                                    <td>
                                        <Badge color={parseFloat(res.moyenne) >= 10 ? "success" : "danger"} outline className="p-2">
                                            {parseFloat(res.moyenne).toFixed(2)} / 20
                                        </Badge>
                                    </td>
                                    <td>
                                        <span className="fw-bold">{res.total_credits_validated || res.total_credits || 0}</span> / 60
                                    </td>
                                    <td>
                                        <Badge color="secondary" pill outline>
                                            {res.nb_inscriptions}<sup>e</sup> Inscription
                                        </Badge>
                                    </td>
                                    <td>
                                        <Badge color={res.status_color} className="p-2" style={{ minWidth: "100px" }}>
                                            {res.final_status}
                                        </Badge>
                                    </td>
                                    <td>
                                        <Button color="light" size="sm" className="text-primary">
                                            <i className="bx bx-file-blank fs-5"></i>
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6" className="text-center text-muted p-4">
                                    Aucune donnée à délibérer.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>
        </React.Fragment>
    );
};

export default FinalDeliberationManager;